import { CPLAT_API_ROUTES, usePost } from '@/api';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { useAppDispatch } from '@/redux/store';
import { initUser, setUser } from '@/redux/userSlice';
import { ROUTES } from '@/routes';
import { Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useLocalStorage } from 'usehooks-ts';
import { AppInner } from './app-inner';
import { UpdateAccountDialogProvider } from './contexts/UpdateAccountDialogContext';
import { RegisterTokenProvider } from './contexts/register-token';
import {
  completeMobileMembershipMockAuthentication,
  getIsMobileMembershipMockEnabled,
  getMobileMembershipMockUser,
  getShouldUseMobileMembershipMock,
  MOBILE_MEMBERSHIP_MOCK_TOKEN,
} from './pages/mobile-membership/mobile-membership-mock';
import { setinquiryParams } from './redux/inquirySlice';
import {
  deviceUuidState,
  gameCategoryState,
  gameScoreState,
  initialRequestState,
  isMobileState,
  recentSearchKeywordListAsyncStorageState,
  safeAreaInsetsState,
  templateInquiryState,
} from './store/atom';
import { CplatApiResponse } from './types/api';
import { AES } from './utils/AES';

type AuthenticatedUser = {
  PhoneNumber?: string;
};

/**
 * 웹에서 init프로세스를 관리하는 페이지
 */
const App = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const dispatch = useAppDispatch();
  const [localStorageCplatToken, setLocalStorageCplatToken] = useLocalStorage('cplatToken', '');
  const [init, setInit] = useState(false);
  const setInquiryCommentTemplate = useSetRecoilState(templateInquiryState);
  const [safeAreaInsets, setSafeAreaInsets] = useRecoilState(safeAreaInsetsState);
  const setGameCategory = useSetRecoilState(gameCategoryState);
  const setGameScore = useSetRecoilState(gameScoreState);
  const setRecentSearchKeywordList = useSetRecoilState(recentSearchKeywordListAsyncStorageState);
  const setInitialRequest = useSetRecoilState(initialRequestState);
  const setIsMobile = useSetRecoilState(isMobileState);
  const setDeviceUuid = useSetRecoilState(deviceUuidState);

  // query string 조회
  const [searchParams, setSearchParams] = useSearchParams();
  const queryStringCplatToken = searchParams.get('cplatToken');
  const inquiryParams = searchParams.get('inquiryParams');
  const templateInquiryData = searchParams.get('templateInquiryData');
  const safeAreaInsetsData = searchParams.get('safeAreaInsets');
  const gameCategory = searchParams.get('gameCategory');
  const gameScore = searchParams.get('gameScore');
  const recentSearchKeywordListAsyncStorage = searchParams.get(
    'recentSearchKeywordListAsyncStorage',
  );
  const initialRequest = searchParams.get('initialRequest');
  const isMobile = searchParams.get('isMobile');
  const deviceUuid = searchParams.get('deviceUuid');

  useEffect(() => {
    if (inquiryParams) {
      const parsedParams = JSON.parse(inquiryParams);
      dispatch(setinquiryParams(parsedParams));
    }
    if (templateInquiryData !== null && templateInquiryData !== 'undefined') {
      const parsedParams = JSON.parse(templateInquiryData);
      setInquiryCommentTemplate(parsedParams);
    }
    if (safeAreaInsetsData !== null && safeAreaInsetsData !== 'undefined') {
      const parsedParams = JSON.parse(safeAreaInsetsData);
      setSafeAreaInsets(parsedParams);
    }
    if (gameCategory) {
      setGameCategory(gameCategory);
    }
    if (gameScore) {
      setGameScore(gameScore);
    }
    if (
      recentSearchKeywordListAsyncStorage !== null &&
      recentSearchKeywordListAsyncStorage.length > 0
    ) {
      const parsedParams = JSON.parse(recentSearchKeywordListAsyncStorage);
      setRecentSearchKeywordList(parsedParams);
    }
    if (initialRequest !== null) {
      setInitialRequest(initialRequest);
    }
    if (isMobile) {
      setIsMobile(isMobile === 'true');
    }
    if (deviceUuid) {
      setDeviceUuid(deviceUuid);
    }
  }, [
    dispatch,
    inquiryParams,
    isMobile,
    templateInquiryData,
    safeAreaInsetsData,
    gameCategory,
    gameScore,
    recentSearchKeywordListAsyncStorage,
    initialRequest,
    deviceUuid,
    setDeviceUuid,
    setGameCategory,
    setGameScore,
    setInitialRequest,
    setInquiryCommentTemplate,
    setIsMobile,
    setRecentSearchKeywordList,
    setSafeAreaInsets,
  ]);

  const isKakaoCallback = pathname === `/${ROUTES.kakaoCallback}`;
  const marketConnectBasePath = ROUTES.marketConnectMarketName.replace('/:marketName', '');
  const isSigninPage =
    pathname.startsWith(ROUTES.signin) || pathname.startsWith(ROUTES.legacySignin);
  const isMobileRoute = pathname.startsWith(`${ROUTES.mobileRoot}/`);
  const isMobileSigninPage = pathname === ROUTES.mobileSignin;
  const isMobileSignupPage = pathname === ROUTES.mobileSignup;
  const isMobileResetPasswordPage = pathname === ROUTES.mobileResetPassword;
  const isMobilePublicPage =
    isMobileSigninPage ||
    isMobileSignupPage ||
    isMobileResetPasswordPage ||
    pathname === ROUTES.mobileMembership;
  const isSignupPage = pathname.startsWith(ROUTES.signup);
  const isIntroPage = pathname.startsWith(ROUTES.intro);
  const isResetPasswordPage = pathname.startsWith(ROUTES.resetPassword);
  const isUpdateAccountPage = pathname.startsWith(ROUTES.updateAccount);
  const currentPathWithSearch = `${pathname}${search}`;
  const mobileSigninRedirectPath = `${ROUTES.mobileSignin}?redirect=${encodeURIComponent(
    currentPathWithSearch,
  )}`;

  const isRegisterMarketPage =
    pathname.startsWith(ROUTES.naverRegisterStart) ||
    pathname.startsWith(ROUTES.naverRegisterToken) ||
    pathname.startsWith(ROUTES.cafe24RegisterStart) ||
    pathname.startsWith(ROUTES.imwebRegisterStart) ||
    pathname.startsWith(ROUTES.shopbyRegisterStart) ||
    pathname.startsWith(marketConnectBasePath);
  const { mutate: getUserInfo } = usePost(CPLAT_API_ROUTES.getUserInfo);
  useEffect(() => {
    if (queryStringCplatToken) {
      localStorage.setItem('cplatToken', JSON.stringify(queryStringCplatToken));
      setLocalStorageCplatToken(queryStringCplatToken);

      // 쿼리스트링에서 cplatToken 삭제
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('cplatToken');

      if (isMobileRoute) {
        setInit(true);
        const timeoutId = window.setTimeout(() => setSearchParams(newSearchParams), 300);

        return () => window.clearTimeout(timeoutId);
      }

      setSearchParams(newSearchParams);
    }
    // eslint-disable-next-line
  }, [queryStringCplatToken]);

  useEffect(() => {
    if (localStorageCplatToken === undefined) return; // 아직 초기화 전

    // 로그인/회원가입/콜백 페이지 등은 패스
    if (
      isKakaoCallback ||
      isSigninPage ||
      isMobilePublicPage ||
      isSignupPage ||
      isResetPasswordPage ||
      isUpdateAccountPage ||
      isIntroPage ||
      isRegisterMarketPage
    ) {
      setInit(true);
      return;
    }

    if (searchParams.get('cplatToken')) {
      return;
    }

    if (isMobileRoute && getShouldUseMobileMembershipMock(searchParams)) {
      completeMobileMembershipMockAuthentication();
      dispatch(setUser(getMobileMembershipMockUser()));
      setInit(true);
      return;
    }

    if (!localStorageCplatToken) {
      // useLocalStorage의 state가 아직 동기화 안 됐을 수 있으므로 직접 확인
      const directToken = localStorage.getItem('cplatToken');
      if (directToken && directToken !== '""' && directToken !== '') {
        const normalizedDirectToken = directToken.replace(/^['"]|['"]$/g, '');
        if (
          getShouldUseMobileMembershipMock(searchParams) &&
          normalizedDirectToken === MOBILE_MEMBERSHIP_MOCK_TOKEN
        ) {
          dispatch(setUser(getMobileMembershipMockUser()));
          setInit(true);
        }
        return; // 토큰이 실제로 있으면 리다이렉트 하지 않음
      }
      setTimeout(() => setInit(true), 600);
      if (isMobileRoute) {
        navigate(mobileSigninRedirectPath);
        return;
      }
      const redirectPath = pathname !== '/' ? `&redirect=${encodeURIComponent(pathname)}` : '';
      navigate(`${ROUTES.signin}?ctab=init${redirectPath}`);
      return;
    }

    // -----------------------------------------------------------
    //  토큰 검증 및 마이그레이션 로직
    // -----------------------------------------------------------

    const rawToken =
      typeof localStorageCplatToken === 'string'
        ? localStorageCplatToken.replace(/^['"]|['"]$/g, '')
        : localStorageCplatToken;

    let tokenToUse = rawToken;

    // 1. JWT 형식이 아닌 경우 (암호화된 토큰일 확률 높음)
    if (!isJwtToken(localStorageCplatToken)) {
      try {
        // 복호화 시도
        const decrypted = AES.decryptData(localStorageCplatToken);

        // 복호화했더니 유효한 JWT라면? -> 로컬스토리지에 '원본'으로 다시 저장 (마이그레이션)
        if (isJwtToken(decrypted)) {
          tokenToUse = decrypted;
          setLocalStorageCplatToken(decrypted); // 이제 암호화 안 된걸로 덮어씌움
        } else {
          // 복호화 했는데도 이상하면 그냥 에러 처리
          throw new Error('Invalid Decrypted Token');
        }
      } catch (e) {
        // 복호화 실패 혹은 이상한 토큰 -> 미련없이 지우고 로그인으로 보냄
        console.warn('Legacy token cleanup');
        localStorage.removeItem('cplatToken');
        setLocalStorageCplatToken(''); // hook 상태도 초기화
        navigate(isMobileRoute ? mobileSigninRedirectPath : ROUTES.signin);
        return;
      }
    }
    // 이제 tokenToUse는 무조건 Raw Token임이 보장됨
    if (
      (getIsMobileMembershipMockEnabled() || getShouldUseMobileMembershipMock(searchParams)) &&
      tokenToUse === MOBILE_MEMBERSHIP_MOCK_TOKEN
    ) {
      dispatch(setUser(getMobileMembershipMockUser()));
      setInit(true);
      return;
    }

    getUserInfo(
      { cplatToken: tokenToUse },
      {
        onSuccess(res) {
          const { data, code } = res.data as CplatApiResponse<AuthenticatedUser[]>;

          if (code === '200') {
            const user = data[0];
            dispatch(setUser(user));

            if (pathname === '/') {
              if (user.PhoneNumber === '01093483900') {
                navigate(ROUTES.admin);
              } else {
                navigate(ROUTES.dashboard);
              }
            } else if (pathname === ROUTES.admin) {
              if (user.PhoneNumber !== '01093483900') {
                localStorage.removeItem('cplatToken');
                dispatch(initUser());
                navigate(ROUTES.signin);
              }
            } else {
              const searchParamsToKeep = new URLSearchParams();
              searchParams.forEach((value, key) => {
                if (!USED_QUERY_KEYS.includes(key)) {
                  searchParamsToKeep.set(key, value);
                }
              });
              navigate({
                pathname,
                search: searchParamsToKeep.toString(),
              });
            }
          } else {
            localStorage.removeItem('cplatToken');
            navigate(isMobileRoute ? mobileSigninRedirectPath : ROUTES.signin);
          }
          setTimeout(() => setInit(true), 400);
        },
        onError() {
          localStorage.removeItem('cplatToken');
          navigate(isMobileRoute ? mobileSigninRedirectPath : ROUTES.signin);
        },
      },
    );
    // eslint-disable-next-line
  }, [
    isKakaoCallback,
    isSigninPage,
    isMobilePublicPage,
    isMobileRoute,
    mobileSigninRedirectPath,
    localStorageCplatToken,
    searchParams,
    pathname,
    getUserInfo,
    dispatch,
    setLocalStorageCplatToken,
    navigate,
  ]);

  useEffect(() => {
    if (!localStorageCplatToken?.indexOf('"')) setLocalStorageCplatToken('');
  }, [localStorageCplatToken, setLocalStorageCplatToken]);

  return (
    <Suspense fallback={<GlobalSpinner />}>
      <RegisterTokenProvider>
        <UpdateAccountDialogProvider>
          <AppInner init={init} safeAreaInsets={safeAreaInsets} />
        </UpdateAccountDialogProvider>
      </RegisterTokenProvider>
    </Suspense>
  );
};

export default App;

const USED_QUERY_KEYS = [
  'tab',
  'cplatToken',
  'inquiryParams',
  'templateInquiryData',
  'safeAreaInsets',
  'gameCategory',
  'gameScore',
  'recentSearchKeywordListAsyncStorage',
  'initialRequest',
  'isMobile',
  'deviceUuid',
];

function isJwtToken(token: string) {
  // JWT는 보통 .(dot)으로 2번 구분됨 (header.payload.signature)
  return typeof token === 'string' && token.split('.').length === 3;
}
